import {useMemo} from 'react'
import {graphql, usePaginationFragment} from 'react-relay'

import type {TabbedTimelineFragment$data} from './__generated__/TabbedTimelineFragment.graphql'
import type {useConversationTimelineItemsBackFragment$key} from './__generated__/useConversationTimelineItemsBackFragment.graphql'
import type {
  IssueTimelineItemsItemType,
  useConversationTimelineItemsBackPaginationQuery,
} from './__generated__/useConversationTimelineItemsBackPaginationQuery.graphql'
import type {useConversationTimelineItemsFrontFragment$key} from './__generated__/useConversationTimelineItemsFrontFragment.graphql'
import type {useConversationTimelineItemsFrontPaginationQuery} from './__generated__/useConversationTimelineItemsFrontPaginationQuery.graphql'
import {createBackFetcherConfig, useTimelineItemsCore, type UseTimelineItemsCoreResult} from './use-timeline-items-core'

// ─── Fragments ──────────────────────────────────────────────────────────────

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const conversationFrontFragment = graphql`
  fragment useConversationTimelineItemsFrontFragment on Issue
  @argumentDefinitions(
    count: {type: "Int", defaultValue: 15}
    cursor: {type: "String"}
    itemTypes: {type: "[IssueTimelineItemsItemType!]"}
  )
  @refetchable(queryName: "useConversationTimelineItemsFrontPaginationQuery") {
    conversationFrontItems: timelineItems(first: $count, after: $cursor, itemTypes: $itemTypes)
      @defer(label: "Issue__conversationFrontItems")
      @connection(key: "Issue__conversationFrontItems") {
      pageInfo {
        hasNextPage
      }
      totalCount
      edges {
        node {
          __id
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...IssueTimelineItem
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const conversationBackFragment = graphql`
  fragment useConversationTimelineItemsBackFragment on Issue
  @argumentDefinitions(
    count: {type: "Int", defaultValue: 0}
    cursor: {type: "String"}
    itemTypes: {type: "[IssueTimelineItemsItemType!]"}
  )
  @refetchable(queryName: "useConversationTimelineItemsBackPaginationQuery") {
    conversationBackItems: timelineItems(last: $count, before: $cursor, itemTypes: $itemTypes)
      @defer(label: "Issue__conversationBackItems")
      @connection(key: "Issue__conversationBackItems") {
      pageInfo {
        hasPreviousPage
      }
      totalCount
      edges {
        node {
          __id
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...IssueTimelineItem
        }
      }
    }
  }
`

// Custom query for loading more back items — same pattern as the classic timeline.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const conversationBackQuery = graphql`
  query useConversationTimelineItemsBackQuery(
    $id: ID!
    $count: Int!
    $cursor: String
    $itemTypes: [IssueTimelineItemsItemType!]
  ) {
    node(id: $id) {
      ... on Issue {
        conversationBackItems: timelineItems(last: $count, before: $cursor, itemTypes: $itemTypes) {
          pageInfo {
            startCursor
          }
          totalCount
          edges {
            node {
              __id
              # eslint-disable-next-line relay/must-colocate-fragment-spreads
              ...IssueTimelineItem
            }
          }
        }
      }
    }
  }
`

// ─── Hook ───────────────────────────────────────────────────────────────────

type UseConversationTimelineItemsProps = {
  timelineData: TabbedTimelineFragment$data
  itemTypes: IssueTimelineItemsItemType[]
  highlightedEvent?: string | undefined
  page?: number
}

/**
 * Hook for fetching and paginating conversation timeline items.
 *
 * Uses `itemTypes` to filter timeline events server-side. Delegates pagination,
 * back-loading, highlight support, deduplication, and rollups to the shared
 * `useTimelineItemsCore` hook.
 *
 * Used by the Conversation tab in the tabbed timeline.
 */
export function useConversationTimelineItems({
  timelineData,
  itemTypes,
  highlightedEvent,
  page,
}: UseConversationTimelineItemsProps): UseTimelineItemsCoreResult {
  const {
    data: {conversationFrontItems: frontData},
    loadNext: loadMoreFrontItems,
  } = usePaginationFragment<
    useConversationTimelineItemsFrontPaginationQuery,
    useConversationTimelineItemsFrontFragment$key
  >(conversationFrontFragment, timelineData)

  const {
    data: {conversationBackItems: backData},
  } = usePaginationFragment<
    useConversationTimelineItemsBackPaginationQuery,
    useConversationTimelineItemsBackFragment$key
  >(conversationBackFragment, timelineData)

  const backFetcherConfig = useMemo(
    () => ({
      ...createBackFetcherConfig(conversationBackQuery, 'conversationBackItems'),
      // Override makeVariables to include itemTypes
      makeVariables: (id: string, count: number, cursor: string | null) => ({
        id,
        count,
        cursor,
        itemTypes,
      }),
    }),
    [itemTypes],
  )

  return useTimelineItemsCore({
    issueId: timelineData.id,
    frontData,
    backData,
    loadMoreFrontItems,
    backFetcherConfig,
    highlightedEvent,
    page,
    itemTypes,
  })
}
